import { useMemo } from "react";
import dayjs from "dayjs";
import { BarSeriesType } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-date-pickers/internals";
import { TFunction } from "i18next";
import Group from "../../../models/Group";
import {
  DashboardResults,
  EmissionTableRow,
  MonthlyData,
  PreviousYearData,
  SCOPE_MAPPING,
  ScopeBreakdown,
  YearlyData,
} from "../../../models/emission-dashboard.types";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getScopeKey = (
  groupId: string,
): keyof ScopeBreakdown | undefined => {
  return SCOPE_MAPPING[groupId];
};

export const getScopeValue = (
  data: MonthlyData | PreviousYearData | YearlyData | undefined,
  scopeKey: keyof ScopeBreakdown | undefined,
  isScopeView: boolean,
  typeId?: string,
) => {
  if (!data) return 0;
  if (isScopeView) {
    if (!("types" in data)) return 0;
    return data.types?.find((s) => s.categoryEn === typeId)?.value ?? 0;
  } else {
    if (!scopeKey || !data[scopeKey]) return 0;
    return data[scopeKey].value;
  }
};

interface UseEmissionCardDataProps {
  data: DashboardResults | null;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  groups: Group[];
  scope?: "Scope-1" | "Scope-2" | "Scope-3" | "Outside-of-Scope";
  t: TFunction<[string, string], undefined>;
}

export const useEmissionCardData = ({
  data,
  startDate,
  endDate,
  groups,
  scope,
  t,
}: UseEmissionCardDataProps) => {
  return useMemo(() => {
    const startYear = startDate.year();
    const endYear = endDate.year();
    const activeLabels: string[] = [];
    const activeSeries: MakeOptional<BarSeriesType, "type">[] = [];
    const rows: any[] = [];
    let isYoYAvailable = false;
    const isScopeView = !!scope;

    if (!data)
      return { labels: [], chartSeries: [], tableRows: [], showYoY: false };

    if (startYear === endYear) {
      // --- MONTHLY VIEW ---
      isYoYAvailable = true;
      let sourceData: MonthlyData[] = [];

      if (isScopeView && scope) {
        const scopeKey = SCOPE_MAPPING[scope];
        const scopeSpecificData = data.byScopes[scopeKey]?.monthly ?? [];
        sourceData = Array.isArray(scopeSpecificData) ? scopeSpecificData : [];
      } else {
        sourceData = Array.isArray(data.monthly) ? data.monthly : [];
      }

      const dataMap = new Map<number, MonthlyData>();
      sourceData.forEach((item) => dataMap.set(item.month - 1, item));

      const chartData = {
        current: {} as Record<string, number[]>,
        prev1: {} as Record<string, number[]>,
        prev2: {} as Record<string, number[]>,
      };

      groups.forEach((g) => {
        chartData.current[g.id] = [];
        chartData.prev1[g.id] = [];
        chartData.prev2[g.id] = [];
      });

      let currentDate = startDate.clone().startOf("month");
      const stopDate = endDate.clone().endOf("month");

      while (
        currentDate.isBefore(stopDate) ||
        currentDate.isSame(stopDate, "month")
      ) {
        const monthIndex = currentDate.month();
        const matchedData = dataMap.get(monthIndex);

        activeLabels.push(t(monthNames[monthIndex]));

        rows.push({
          label: `${t(monthNames[monthIndex])}`,
          fullLabel: `${t(monthNames[monthIndex])} ${currentDate.year()}`,
          total: matchedData?.total || 0,
          scopes: matchedData || {},
          previousTotal: matchedData?.previous_1year?.total || 0,
          yoy: matchedData?.yoy_percent ?? null,
        });

        groups.forEach((group) => {
          let shouldInclude = true;
          if (shouldInclude) {
            const scopeKey = scope
              ? SCOPE_MAPPING[scope]
              : getScopeKey(group.id);
            chartData.current[group.id].push(
              getScopeValue(
                matchedData,
                scopeKey,
                isScopeView,
                scope ? group.id : undefined,
              ),
            );
            chartData.prev1[group.id].push(
              getScopeValue(
                matchedData?.previous_1year,
                scopeKey,
                isScopeView,
                scope ? group.id : undefined,
              ),
            );
            chartData.prev2[group.id].push(
              getScopeValue(
                matchedData?.previous_2year,
                scopeKey,
                isScopeView,
                scope ? group.id : undefined,
              ),
            );
          } else {
            chartData.current[group.id].push(0);
            chartData.prev1[group.id].push(0);
            chartData.prev2[group.id].push(0);
          }
        });

        currentDate = currentDate.add(1, "month");
      }

      groups.forEach((group) => {
        activeSeries.push({
          data: chartData.prev2[group.id],
          label: t(group.title),
          stack: (startYear - 2).toString(),
          color: group.colors[0],
          id: `${group.id}_prev2`,
        });
        activeSeries.push({
          data: chartData.prev1[group.id],
          label: t(group.title),
          stack: (startYear - 1).toString(),
          color: group.colors[0],
          id: `${group.id}_prev1`,
        });
        activeSeries.push({
          data: chartData.current[group.id],
          label: t(group.title),
          stack: startYear.toString(),
          color: group.colors[1],
          id: group.id,
        });
      });
    } else {
      // --- YEARLY VIEW ---
      const scopeSpecificData: MonthlyData[] | YearlyData[] | null = scope
        ? data.byScopes[SCOPE_MAPPING[scope]]?.monthly
        : data.yearly;

      const sourceData: MonthlyData[] | YearlyData[] = Array.isArray(
        scopeSpecificData,
      )
        ? scopeSpecificData
        : [];

      if (sourceData.length > 0) {
        let years = [];
        for (let i = 0; i < endDate.year() - startDate.year() + 1; i++) {
          years.push(`${i + startDate.year()}`);
        }
        const uniqueYears = years.filter(
          (value, index, array) => array.indexOf(value) === index,
        );
        activeLabels.push(...uniqueYears);
      }

      groups.forEach((group) => {
        if (scope) {
          const temp: MonthlyData[] = sourceData as MonthlyData[];
          const years: number[] = [];
          for (let i = 0; i < endDate.year() - startDate.year() + 1; i++) {
            years.push(i + startDate.year());
          }

          activeSeries.push({
            data: years.map((year) => {
              let total = 0;
              temp
                .filter((i) => i.year === year)
                .forEach((i) => {
                  total +=
                    i.types?.find((t) => t.categoryEn === group.id)?.value ?? 0;
                });
              return total;
            }),
            label: group.title,
            stack: `${years}`,
            color: group.colors[1],
            id: group.id,
          });
        } else {
          const scopeKey = getScopeKey(group.id);
          const groupData = activeLabels.map((y) => {
            const item = sourceData.find((i) => `${i.year}` === `${y}`);
            if (!item) return 0;
            return getScopeValue(item, scopeKey, false);
          });

          if (isScopeView && scope && group.id !== scope) {
            groupData.fill(0);
          }

          activeSeries.push({
            data: groupData,
            label: t(group.title),
            stack: "total",
            color: group.colors[1],
            id: group.id,
          });
        }
      });

      activeLabels.forEach((y) => {
        const item = sourceData.find((i) => `${i.year}` === `${y}`);
        rows.push({
          label: y,
          fullLabel: y,
          total: item?.total ?? 0,
          scopes: item ?? { s0: 0, s1: 0, s2: 0, s3: 0 },
          previousTotal: 0,
          yoy: null,
        });
      });
    }

    return {
      labels: activeLabels,
      chartSeries: activeSeries,
      tableRows: rows,
      showYoY: isYoYAvailable,
    };
  }, [data, startDate, endDate, groups, scope, t]);
};
